import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { ContactStatus, type ContactAnalyticsResponse } from '@signalvote/shared';
import { CollapsibleSection } from './CollapsibleSection';
import { Skeleton } from '../ui/skeleton';

// ============================================================================
// Types
// ============================================================================

interface VendorContactOutcomesProps {
  vendorOutcomes: ContactAnalyticsResponse['contactsByStatus'];
  campaignOutcomes: ContactAnalyticsResponse['contactsByStatus'];
  isLoading?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const STATUS_LABELS: Record<string, string> = {
  Reached: 'Reached',
  NotHome: 'Not Home',
  Refused: 'Refused',
  NeedsFollowUp: 'Needs Follow Up',
};

const ALL_STATUSES = Object.values(ContactStatus);

const VENDOR_COLOR = '#6366f1'; // indigo-500
const CAMPAIGN_COLOR = '#d1d5db'; // gray-300

// ============================================================================
// Helpers
// ============================================================================

function buildChartData(
  vendorOutcomes: ContactAnalyticsResponse['contactsByStatus'],
  campaignOutcomes: ContactAnalyticsResponse['contactsByStatus']
) {
  const vendorMap = new Map(vendorOutcomes.map((o) => [o.status, o]));
  const campaignMap = new Map(campaignOutcomes.map((o) => [o.status, o]));

  return ALL_STATUSES.map((status) => ({
    status,
    label: STATUS_LABELS[status] ?? status,
    vendor: vendorMap.get(status)?.percentage ?? 0,
    vendorCount: vendorMap.get(status)?.count ?? 0,
    campaign: campaignMap.get(status)?.percentage ?? 0,
    campaignCount: campaignMap.get(status)?.count ?? 0,
  }));
}

// ============================================================================
// Component
// ============================================================================

export function VendorContactOutcomes({
  vendorOutcomes,
  campaignOutcomes,
  isLoading,
}: VendorContactOutcomesProps) {
  if (isLoading) {
    return (
      <CollapsibleSection sectionId="vendor-contact-outcomes" title="Contact Outcomes">
        <div className="space-y-4 pt-2">
          <Skeleton className="h-48" />
          <div className="flex gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </CollapsibleSection>
    );
  }

  const hasVendorData = vendorOutcomes.length > 0;
  const hasCampaignData = campaignOutcomes.length > 0;
  const chartData = buildChartData(vendorOutcomes, campaignOutcomes);

  return (
    <CollapsibleSection sectionId="vendor-contact-outcomes" title="Contact Outcomes">
      {!hasVendorData ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No contact outcome data for this vendor
        </p>
      ) : (
        <div className="space-y-4 pt-2">
          {/* Legend */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: VENDOR_COLOR }} />
              <span>This Vendor</span>
            </div>
            {hasCampaignData && (
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: CAMPAIGN_COLOR }} />
                <span>Campaign Average</span>
              </div>
            )}
          </div>

          {/* Grouped Bar Chart */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis
                  tickFormatter={(v: number) => `${v}%`}
                  tick={{ fontSize: 12 }}
                  domain={[0, 'auto']}
                />
                <Tooltip
                  formatter={(value, name, props) => {
                    const numValue = Number(value);
                    const count = name === 'vendor'
                      ? (props.payload as { vendorCount: number; campaignCount: number }).vendorCount
                      : (props.payload as { vendorCount: number; campaignCount: number }).campaignCount;
                    return [`${numValue.toFixed(1)}% (${count})`, name === 'vendor' ? 'This Vendor' : 'Campaign Avg'];
                  }}
                />
                <Bar dataKey="vendor" fill={VENDOR_COLOR} radius={[4, 4, 0, 0]} />
                {hasCampaignData && (
                  <Bar dataKey="campaign" fill={CAMPAIGN_COLOR} radius={[4, 4, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Detail Legend — vendor vs campaign per category */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            {chartData.map((item) => (
              <div key={item.status}>
                <p className="text-muted-foreground">{item.label}</p>
                <p className="font-medium tabular-nums">
                  {item.vendor.toFixed(1)}%
                  <span className="ml-1 text-xs text-muted-foreground">({item.vendorCount})</span>
                </p>
                {hasCampaignData && (
                  <p className="text-xs tabular-nums text-muted-foreground">
                    vs {item.campaign.toFixed(1)}%
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}
